import React, { useContext, useState } from 'react'
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material'
import { VideoLibrary } from '@mui/icons-material'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DialogUploadVideo from './Dialog'
import PlayVideo from './Video'
import { OfrecerPremiumContext } from '../../../../context/OfrecerPremium/ofrecerPremium.context'
import { useAnalytics } from '../../../../hooks/useAnalytics'
import { Error as ErrorIcon } from '@mui/icons-material'
import SnackBarUtils from '../../../../utils/SnackBarUtils'

interface Props {
  subeVideo: boolean
  maxSize: string
  maxDuration: string
  videoData: any
  ofrecerPremium: boolean
  setVideoData: (video: any) => void
}

const UploadVideo: React.FC<Props> = ({
  subeVideo,
  maxDuration,
  maxSize,
  videoData,
  ofrecerPremium,
  setVideoData
}: Props) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [openVideo, setOpenVideo] = useState<boolean>(false)
  const [error, setError] = useState<string>(null)
  const { onOpen } = useContext(OfrecerPremiumContext)
  const { track } = useAnalytics()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setVideoData(null)
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')} seg`
  }

  const formatFileSize = (bytes: number) => {
    return (bytes / (1024 * 1024)).toFixed(1) + ' mb'
  }

  const handleOfrecerPremium = () => {
    track('vendor_premium_popup_abrir', {
      origin: 'abm_video'
    })
    onOpen()
  }
  return (
    <>
      {videoData ? (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: 2,
            backgroundColor: '#2d2d2d',
            borderRadius: 2,
            padding: '12px 16px',
            position: 'relative'
          }}
        >
          <Box
            onClick={() => setOpenVideo(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#4a4a4a',
              borderRadius: 1,
              width: 60,
              height: 60,
              flexShrink: 0,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <video
              src={videoData.url}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
              }}
            >
              <VideoLibrary sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: '#fff',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '4px'
              }}
            >
              {videoData.file.name}
            </Typography>
            <Typography
              sx={{
                color: '#999',
                fontSize: '13px'
              }}
            >
              {formatDuration(videoData.duration)} - {formatFileSize(videoData.file.size)}
            </Typography>
          </Box>

          <IconButton
            onClick={handleDelete}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <DeleteOutlineOutlinedIcon />
          </IconButton>
        </Box>
      ) : (
        <Box
          onClick={() => (subeVideo ? setOpenDialog(true) : ofrecerPremium && handleOfrecerPremium())}
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: 2,
            backgroundColor: '#2d2d2d',
            borderRadius: 2,
            padding: '12px 16px',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: '#3d3d3d'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#ff8c42',
              borderRadius: 1.5,
              width: 48,
              height: 48,
              flexShrink: 0
            }}
          >
            <VideoLibrary sx={{ color: '#000', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: '#fff',
                fontSize: '16px',
                fontWeight: 500,
                marginBottom: '2px'
              }}
            >
              Subir video
              <Typography
                component="span"
                sx={{
                  color: '#ff8c42',
                  fontSize: '16px',
                  fontWeight: 500,
                  marginLeft: '4px'
                }}
              >
                (sólo para Premium Oro Plus)
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: '#999',
                fontSize: '14px'
              }}
            >
              Peso máximo {maxSize}mb
            </Typography>
          </Box>
        </Box>
      )}
      {!isMobile && error && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: '#ffebee',
            padding: '12px',
            borderRadius: '12px',
            mt: 2
          }}
        >
          <ErrorIcon sx={{ color: '#d32f2f', fontSize: 24 }} />
          <Typography
            sx={{
              fontSize: '14px',
              color: '#d32f2f'
            }}
          >
            {error}
          </Typography>
        </Box>
      )}

      <DialogUploadVideo
        open={openDialog}
        setOpen={setOpenDialog}
        maxDuration={maxDuration}
        maxSize={maxSize}
        setVideoData={setVideoData}
        error={error}
        setError={setError}
      />
      <PlayVideo url={videoData?.url} open={openVideo} setOpen={setOpenVideo} />
    </>
  )
}

export default UploadVideo
